import React, { useMemo } from 'react';
import { SellingOffersNegotiationData } from '@interfaces/models/negotiation/domain/SellingOffersNegotiation';
import Link from 'next/link';
import VCImage from '@components/atoms/vc-images/vc-image';
import { useGetNegotiationStatus } from '@hooks/negotiations/useNegotiationStatus';
import { mapSellingOffersNegotiationDataToNegotiationOfferStatus } from '@interfaces/models/negotiation/mappers/negotiationOfferStatusMapper';
import { AllowedNegotiationActions } from '@interfaces/models/negotiation/negotiation';
import NegotiationListingsCardStatus from '@components/organisms/negotiation-listings/negotiation-listings-card/negotiation-listings-card-status/negotiation-listings-card-status';
import NegotiationListingsCardCta from '@components/organisms/negotiation-listings/negotiation-listings-card/negotiation-listings-card-cta/negotiation-listings-card-cta';
import { useListItemSelectContext } from '@context/negotiation-listings-split-view.context';
import styles from './selling-offers-negotiations-list-card.module.scss';

interface SellingOffersNegotiationListCardProps {
  negotiation: SellingOffersNegotiationData;
  refetch?: () => void;
}

const SellingOffersNegotiationsListCard: React.FC<SellingOffersNegotiationListCardProps> = ({
  negotiation,
  refetch,
}) => {
  const { selectedListItemId: productId } = useListItemSelectContext();
  const negotiationStatusFormatted = mapSellingOffersNegotiationDataToNegotiationOfferStatus(negotiation, true);
  const isLastOffer = useMemo<boolean>(() => {
    const nextActions: AllowedNegotiationActions[] = negotiationStatusFormatted.nextActions || [];
    if (!nextActions.includes('offer')) {
      return false;
    }
    return negotiationStatusFormatted.buyerRemainingOffers === 1;
  }, [negotiation]);

  const offerStatus = useGetNegotiationStatus(negotiationStatusFormatted, true, isLastOffer);

  return (
    <li
      className={styles.sellingOffersCard}
      data-cy="selling_offers_negotiation_card"
    >
      <Link
        href={`/profile/${negotiation.buyer.id}`}
        className={styles.sellingOffersCard__top}
        data-cy="selling_offers_negotiation_buyer_name"
      >
        <div className={styles.sellingOffersCard__profileWrapper}>
          <VCImage
            disableLazyLoad
            src={`/profil/${negotiation.buyer.picturePath}`}
            alt={`${negotiation.buyer.name}`}
            width={40}
            height={40}
          />
        </div>
        <span
          className={styles.sellingOffersCard__profileWrapper__name}
          data-cy="selling_offers_negotiation_buyer_name_label"
        >
          {negotiation.buyer.name}
        </span>
      </Link>

      <div className={styles.sellingOffersCard__bottom}>
        <NegotiationListingsCardStatus
          offerStatus={offerStatus}
          hasBestOffer={negotiationStatusFormatted.hasBestOffer}
          isClosed={negotiationStatusFormatted.isClosed}
        />
        <NegotiationListingsCardCta
          refetch={refetch}
          offerStatus={offerStatus}
          productId={productId}
          negotiationId={negotiationStatusFormatted.id}
          pageType="selling-offers"
        />
      </div>
    </li>
  );
};

export default SellingOffersNegotiationsListCard;
